import {ChecklistIcon, FileIcon, KebabHorizontalIcon, LinkIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {memo, type RefObject, useCallback, useRef} from 'react'

import type {PanelArtifact} from './ArtifactPanelContent'
import styles from './ArtifactRow.module.css'
import type {EditableArtifact} from './create-artifact-dialog-types'
import {safeHref} from './safe-href'
import type {IssueArtifact} from './types'

function ArtifactTypeIcon({type}: {type: IssueArtifact['type']}) {
  const Icon = type === 'plan' ? ChecklistIcon : type === 'link' ? LinkIcon : FileIcon
  return (
    <span className={styles.IconWrap}>
      <Icon size={14} className={styles.Icon} />
    </span>
  )
}

/** Strips protocol and trailing slash so a link's URL reads cleanly as a description. */
function displayUrl(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '')
}

type PersistedArtifactRowProps = {
  id: number
  number: number
  type: IssueArtifact['type']
  name: string
  description?: string
  body: string
  bodyHTML?: string
  onDelete?: (id: number) => void
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLButtonElement | null>) => void
  onSelect?: (artifact: PanelArtifact) => void
}

export const PersistedArtifactRow = memo(function PersistedArtifactRowImpl({
  id,
  number,
  type,
  name,
  description,
  body,
  bodyHTML,
  onDelete,
  onEdit,
  onSelect,
}: PersistedArtifactRowProps) {
  const actionsButtonRef = useRef<HTMLButtonElement>(null)
  const isLink = type === 'link'
  const cleanedUrl = isLink && body ? displayUrl(body) : undefined

  // For links, fall back to the cleaned URL as the title when there's no
  // meaningful name (empty, or the name is just the URL itself).
  const trimmedName = name.trim()
  const linkTitleIsUrl = isLink && (!trimmedName || trimmedName === body.trim() || trimmedName === cleanedUrl)
  const displayName = linkTitleIsUrl ? (cleanedUrl ?? name) : name

  const truncated = description && description.length > 120 ? `${description.slice(0, 120)}…` : description

  const handleClick = useCallback(() => {
    onSelect?.({
      id: `${type}:${id}`,
      type,
      name,
      body,
      bodyHTML: bodyHTML ?? '',
    })
  }, [id, type, name, body, bodyHTML, onSelect])

  const handleDelete = useCallback(() => {
    onDelete?.(id)
  }, [id, onDelete])

  const handleEdit = useCallback(() => {
    onEdit?.({number, type, name, body}, actionsButtonRef)
  }, [body, name, number, onEdit, type])

  const linkHref = isLink ? safeHref(body) : undefined

  return (
    <li className={styles.Row}>
      <ArtifactTypeIcon type={type} />
      <div className={styles.Content}>
        {isLink && linkHref ? (
          <Link className={styles.TitleButton} href={linkHref} target="_blank" rel="noopener noreferrer">
            <span className={styles.Title}>{displayName}</span>
          </Link>
        ) : (
          <button type="button" className={styles.TitleButton} aria-label={displayName} onClick={handleClick}>
            <span className={styles.Title}>{displayName}</span>
          </button>
        )}
        {truncated && <span className={styles.Description}>{truncated}</span>}
      </div>
      <div className={styles.HoverActions}>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton
              ref={actionsButtonRef}
              icon={KebabHorizontalIcon}
              variant="invisible"
              size="small"
              aria-label={`More actions for ${displayName}`}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={handleEdit}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
              <ActionList.Divider />
              <ActionList.Item variant="danger" onSelect={handleDelete}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </li>
  )
})
